import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCandidateAuth } from '../../../contexts/CandidateAuthContext';

const Login = () => {
  const { login, isAuthenticated, loading } = useCandidateAuth();

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated && !loading) {
      window.location.href = '/candidate/dashboard';
    }
  }, [isAuthenticated, loading]);

  // Handle Google Login
  const handleGoogleLogin = () => {
    login();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 md:px-6 pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full border-4 border-[#0096C7] border-t-transparent animate-spin"></div>
          <p className="text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-6 pt-24 pb-16 min-h-screen flex flex-col justify-center">
      {/* SEO Settings */}
      <title>{`Login or Create Your ${process.env.REACT_APP_NAME} Account to Apply for Jobs`}</title>
      <meta name="robots" content="noindex, nofollow" />

      <div className="max-w-xl mx-auto w-full">
        {/* Auth Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#0096C7]/20 to-[#4361EE]/20 flex items-center justify-center">
            <i className="fas fa-lock text-3xl text-[#0096C7]" />
          </div>
          <h1 className="text-2xl md:text-4xl font-bold mb-4">
            Lets Begin Your Journey
          </h1>
          <p className="text-gray-300 text-sm md:text-lg">
            Sign in with Google to get started with Hunar Pool.
          </p>
        </div>
        
        {/* Google Login Card */}
        <div className="section-bg rounded-2xl p-8 border border-gray-800 card-hover">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Sign In with Google</h2>
            <p className="text-gray-400 text-sm">
              Quick, secure access to all Hunar Pool features
            </p>
          </div>
          
          {/* Google Login Button */}
          <button
            onClick={handleGoogleLogin}
            className="google-btn w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-medium py-4 px-6 rounded-xl hover:bg-gray-100 transition-colors duration-200"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="text-lg">Continue with Google</span>
          </button>
          
          <div className="mt-8 pt-8 border-t border-gray-800">
            <div className="text-center">
              <h3 className="font-medium mb-4">Why Google Sign In?</h3>
              <div className="space-y-3 text-sm text-gray-400">
                <div className="flex items-center justify-center">
                  <i className="fas fa-shield-alt text-[#0096C7] mr-3" />
                  <span>Enhanced security with Google's protection</span>
                </div>
                <div className="flex items-center justify-center">
                  <i className="fas fa-bolt text-[#06D6A0] mr-3" />
                  <span>One-click login, no passwords to remember</span>
                </div>
                <div className="flex items-center justify-center">
                  <i className="fas fa-sync text-[#4361EE] mr-3" />
                  <span>Instant access across all your devices</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* New User Message */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            By signing in, you agree to our &nbsp;
            <Link to="/terms-and-conditions" className="text-[#0096C7] hover:underline">
              Terms of Service
            </Link>
             &nbsp;and&nbsp;
            <Link to="/privacy-policy" className="text-[#0096C7] hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;